import {
  ORG_BOOKINGS_TEMPLATES,
  ORG_FEATURES,
  ORG_SETTINGS,
  ORGANISATION_TYPE,
} from '~/constants';
import { useDevFeaturesStore } from '~/store';

import type {
  IFormField,
  IOrganisationNew,
  IOrganisationType,
  Settings,
} from '~/types';

import { addIf } from './useUtilities';

export const TYPE_TO_ORGANISATION_SETTINGS: Record<
  IOrganisationType,
  Settings[]
> = {
  [ORGANISATION_TYPE.CHARTERER]: [
    {
      id: ORG_FEATURES.SAILING_SCHEDULE,
      enabled: true,
    },
    {
      id: ORG_FEATURES.KPIS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.BOOKINGS,
      enabled: false,
    },
    {
      id: ORG_FEATURES.STATISTICS_LAYCAN,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_TRANSIT_TIME,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_SHIPMENT_VOLUMES,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_INVOICES,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_LAYTIME,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_EMISSIONS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_WAITING_FOR_BERTH,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_PRODUCTIVITY,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_BUNKERS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_CLAIMS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.VESSELS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.CARBON_OFFSETTING_WIDGET,
      enabled: true,
    },
    {
      id: ORG_FEATURES.SAILING_SCHEDULE_SYNC,
      enabled: false,
    },
  ],
  [ORGANISATION_TYPE.AGENCY]: [
    {
      id: ORG_FEATURES.SAILING_SCHEDULE,
      enabled: true,
    },
    {
      id: ORG_FEATURES.KPIS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.BOOKINGS,
      enabled: false,
    },
    {
      id: ORG_FEATURES.STATISTICS_LAYCAN,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_TRANSIT_TIME,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_SHIPMENT_VOLUMES,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_INVOICES,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_LAYTIME,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_EMISSIONS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_WAITING_FOR_BERTH,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_PRODUCTIVITY,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_BUNKERS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.STATISTICS_CLAIMS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.VESSELS,
      enabled: true,
    },
    {
      id: ORG_FEATURES.CARBON_OFFSETTING_WIDGET,
      enabled: true,
    },
    {
      id: ORG_FEATURES.SAILING_SCHEDULE_SYNC,
      enabled: false,
    },
  ],
  [ORGANISATION_TYPE.PORT_AGENCY]: [
    { id: ORG_FEATURES.SAILING_SCHEDULE, enabled: true },
    { id: ORG_FEATURES.VESSELS, enabled: true },
    { id: ORG_FEATURES.VOYAGES, enabled: true },
    { id: ORG_FEATURES.SAILING_SCHEDULE_SYNC, enabled: false },
  ],
};

export const getOrganisationNewForm = (): IOrganisationNew => ({
  name: '',
  type: ORGANISATION_TYPE.CHARTERER,
  companies: [],
  settings: TYPE_TO_ORGANISATION_SETTINGS.charterer,
  users: [],
  customerUsers: [],
  bookingTemplate: ORG_BOOKINGS_TEMPLATES.MAIN,
});

export const getOrganisationDefaultSchema = (): Record<string, IFormField> => {
  const agencyOrgIsEnabled = useDevFeaturesStore().AGENCY_ORGANISATION;

  return {
    organisationName: {
      name: 'organisation-name',
      label: 'Organisation Name',
      rules: 'required|min:3|max:128|unique_name',
      component: 'FieldText',
    },
    organisationType: {
      name: 'organisation-type',
      label: '',
      rules: 'required',
      options: [
        {
          id: ORGANISATION_TYPE.CHARTERER,
          label: 'Charterer',
        },
        {
          id: ORGANISATION_TYPE.AGENCY,
          label: 'Broker',
        },
        ...addIf(agencyOrgIsEnabled, {
          id: ORGANISATION_TYPE.PORT_AGENCY,
          label: 'Agency',
        }),
      ],
      component: 'FieldRadio',
    },
    companies: {
      name: 'organisations',
      label: 'Organisations',
      component: 'CompaniesList',
      rules: 'required',
    },
  };
};

export const getOrganisationEditForm = (name: string): IOrganisationNew => ({
  name,
  type: 'charterer',
  companies: [],
  settings: [],
  users: [],
  customerUsers: [],
  bookingTemplate: ORG_BOOKINGS_TEMPLATES.MAIN,
});

export const getOrganisationFormData = ({
  name,
  companies,
  users,
  customerUsers,
  settings,
  type,
  bookingTemplate,
}: IOrganisationNew) => ({
  name,
  type,
  permissions: settings.filter(({ enabled }) => enabled).map(({ id }) => id),
  companies: companies.map((x) => x.id),
  internal_users: users
    .filter((user) => !user.role.is_admin)
    .map((user) => user.id)
    .sort(),
  customer_users: customerUsers
    .map((x) => ({
      email: x.email,
      role: x.role.id,
    }))
    .sort((a, b) => a.email.localeCompare(b.email)),
  settings: { [ORG_SETTINGS.ORGANISATION_TEMPLATE_TYPE]: bookingTemplate },
});
